import React from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import TableCell from '@mui/material/TableCell'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

/* ─── Design tokens ────────────────────────────────────────────────────────── */
// Same values as the Leadership Dashboard (pages/leadership/index.js), which is
// the visual reference for this page. Kept local so this page does not import
// the dashboard bundle for a handful of constants.
export const PRIMARY       = '#2065D1'
export const PRIMARY_DARK  = '#103996'
export const PRIMARY_LIGHT = '#D1E9FC'
export const PRIMARY_MID   = '#DFE3E8'
export const WARNING       = '#B78103'
export const WARNING_LIGHT = '#FFF7CD'
export const GREEN         = '#047857'
export const GREEN_LIGHT   = '#E6F4EA'
export const RED           = '#B72136'
export const RED_LIGHT     = '#FFE7D9'
export const INK           = '#1a1a2e'
export const MUTED         = '#9a9ab0'
export const LINE          = 'rgba(145,158,171,0.24)'
export const BAR_BG        = '#F4F6F8'

export const cardSx = { background: '#fff', borderRadius: '12px', border: `1px solid ${LINE}`, boxShadow: 'none', overflow: 'hidden' }
export const tabsSx = {
  minHeight: 40,
  '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem', minHeight: 40, textTransform: 'none', color: '#888', px: 1.75, transition: 'color 0.2s' },
  '& .Mui-selected': { color: PRIMARY, fontWeight: 700 },
  '& .MuiTabs-indicator': { background: PRIMARY, height: 3, borderRadius: '3px 3px 0 0' },
}
export const btnSx = { textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px' }

/* ─── KPI tile (same shape as the dashboard's) ─────────────────────────────── */
export function Kpi({ label, value, sub, color = PRIMARY, icon: Icon, onClick }) {
  return (
    <Box onClick={onClick} sx={{
      background: '#fff', borderRadius: '12px', borderTop: `3px solid ${color}`,
      p: '12px 14px', height: '100%', display: 'flex', flexDirection: 'column',
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)', cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: `0 6px 20px ${color}22`, transform: 'translateY(-1px)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minHeight: 26, mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.55, color: MUTED, lineHeight: 1.25 }}>{label}</Typography>
        {Icon && <Box sx={{ p: 0.6, borderRadius: '8px', background: color + '18', display: 'flex', flexShrink: 0 }}><Icon sx={{ fontSize: 15, color }} /></Box>}
      </Box>
      <Box sx={{ mt: 'auto' }}>
        <Typography component='div' sx={{ fontSize: '1.35rem', fontWeight: 800, color: INK, lineHeight: 1.15, letterSpacing: '-0.02em' }}>{value}</Typography>
        <Typography component='div' sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.25, lineHeight: 1.4, height: '1.4em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub != null ? sub : ''}</Typography>
      </Box>
    </Box>
  )
}

/* ─── Section card with a titled header ────────────────────────────────────── */
export function SCard({ title, sub, badge, right, children, sx }) {
  return (
    <Box sx={{ ...cardSx, ...sx }}>
      {(title || right) && (
        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 4, height: 16, borderRadius: 1, background: PRIMARY, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: INK, lineHeight: 1.2 }}>{title}</Typography>
            {sub && <Typography sx={{ fontSize: '0.7rem', color: MUTED, mt: 0.2 }}>{sub}</Typography>}
          </Box>
          {badge != null && <Chip label={badge} size='small' sx={{ height: 20, fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px', background: PRIMARY_LIGHT, color: PRIMARY }} />}
          {right}
        </Box>
      )}
      {children}
    </Box>
  )
}

/* ─── Table header cell ─────────────────────────────────────────────────────── */
export function Th({ children, col, sortCol, sortDir, onSort, right, sx }) {
  const active = col && sortCol === col
  return (
    <TableCell align={right ? 'right' : 'left'} onClick={col && onSort ? () => onSort(col) : undefined} sx={{
      fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
      color: active ? PRIMARY : '#a0a0b8', background: BAR_BG, borderBottom: `2px solid ${PRIMARY_MID}`,
      py: 1.1, whiteSpace: 'nowrap', cursor: col && onSort ? 'pointer' : 'default', position: 'sticky', top: 0, zIndex: 2,
      '&:hover': col && onSort ? { color: PRIMARY } : {}, ...sx,
    }}>
      {children}{active && <span style={{ fontSize: 8, marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </TableCell>
  )
}

/* ─── Small pill ───────────────────────────────────────────────────────────── */
const TONES = {
  primary: { bg: PRIMARY_LIGHT, fg: PRIMARY },
  success: { bg: GREEN_LIGHT, fg: GREEN },
  warning: { bg: WARNING_LIGHT, fg: WARNING },
  error:   { bg: RED_LIGHT, fg: RED },
  neutral: { bg: '#EEF0F3', fg: '#6b6b80' },
}
export function Pill({ tone = 'neutral', children, icon: Icon, title, sx }) {
  const t = TONES[tone] || TONES.neutral
  const el = (
    <Box component='span' sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: '0.64rem', fontWeight: 700, px: 0.9, py: 0.3,
      borderRadius: '6px', letterSpacing: 0.3, background: t.bg, color: t.fg, whiteSpace: 'nowrap', lineHeight: 1.3, ...sx,
    }}>
      {Icon && <Icon sx={{ fontSize: 12 }} />}{children}
    </Box>
  )
  return title ? <Tooltip title={title} arrow>{el}</Tooltip> : el
}

/* ─── Quick filter chip (the dashboard's) ──────────────────────────────────── */
export function QuickChip({ on, danger, onClick, children }) {
  return (
    <Box onClick={onClick} sx={{
      fontSize: '0.7rem', fontWeight: 600, px: 1.1, py: 0.4, borderRadius: '20px', cursor: 'pointer', userSelect: 'none',
      border: `1px solid ${on ? (danger ? RED : PRIMARY) : '#ddd'}`,
      background: on ? (danger ? RED : PRIMARY) : '#fff',
      color: on ? '#fff' : (danger ? RED : '#666'),
      transition: 'all 0.15s',
      '&:hover': { borderColor: danger ? RED : PRIMARY, color: on ? '#fff' : (danger ? RED : PRIMARY), background: on ? undefined : (danger ? RED_LIGHT : PRIMARY_LIGHT) },
    }}>
      {children}
    </Box>
  )
}

/* ─── Label / value pair for detail grids ───────────────────────────────────── */
export function Field({ label, children, mono }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: MUTED, display: 'block', mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.82rem', color: INK, fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word', lineHeight: 1.4 }}>{children ?? '—'}</Typography>
    </Box>
  )
}

/** Compact input styling shared by the filter bars. */
export const inputSx = { '& .MuiOutlinedInput-root': { background: '#fff', borderRadius: '8px', fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }
